<template>
  <div>
    <form class="clearfix" @submit.prevent="getMessages(1)">
      <ul class="filter_form clearfix">
        <li class="filter_form__items">
          <label class="filter_form__label" for="type-filter">{{
            $t('pages.messages.messageFilters.type')
          }}</label>
          <BaseSelect
            v-model="messageParams.type"
            :options="messageTypes"
            :reduce="(option) => option.code"
            id="type-filter"
            clearable
          />
        </li>
        <li class="filter_form__items">
          <label class="filter_form__label">{{ $t('pages.messages.messageFilters.sender') }}</label>
          <input v-model="messageParams.sender" type="text" class="form-control" />
        </li>
        <li class="filter_form__items">
          <label class="filter_form__label" for="status-filter">{{
            $t('pages.messages.messageFilters.status')
          }}</label>
          <BaseSelect
            v-model="messageParams.status"
            :options="messageStatuses"
            :reduce="(option) => option.code"
            id="status-filter"
            clearable
          />
        </li>
        <li class="filter_form__items">
          <label class="filter_form__label">{{
            $t('pages.messages.messageFilters.taskTerm')
          }}</label>
          <div class="row">
            <div class="col-xs-6">
              <BaseDatepicker
                v-model="messageParams.termStart"
                :placeholder="$t('pages.messages.messageFilters.termStart')"
              />
            </div>
            <div class="col-xs-6">
              <BaseDatepicker
                v-model="messageParams.termEnd"
                :placeholder="$t('pages.messages.messageFilters.termEnd')"
              />
            </div>
          </div>
        </li>
        <li class="filter_form__items">
          <label class="filter_form__label" for="status-filter">{{
            $t('pages.messages.messageFilters.category')
          }}</label>
          <BaseSelect
            v-model="messageParams.category"
            :options="messageCategories"
            :reduce="(option) => option.code"
            id="category-filter"
            clearable
          />
        </li>
        <li class="filter_form__items">
          <label class="filter_form__label">{{
            $t('pages.messages.messageFilters.messageReceptionPeriod')
          }}</label>
          <div class="row">
            <div class="col-xs-6">
              <BaseDatepicker
                v-model="messageParams.dateFrom"
                :placeholder="$t('pages.messages.messageFilters.termStart')"
              />
            </div>
            <div class="col-xs-6">
              <BaseDatepicker
                v-model="messageParams.dateTo"
                :placeholder="$t('pages.messages.messageFilters.termEnd')"
              />
            </div>
          </div>
        </li>
        <li class="filter_form__items">
          <label class="filter_form__label">{{
            $t('pages.messages.messageFilters.subject')
          }}</label>
          <input v-model="messageParams.subject" type="text" class="form-control" />
        </li>

        <li class="filter_form__items filter_form__items--full">
          <button
            :disabled="loadingMessages"
            type="submit"
            class="btn btn-feat btn-mobile-full pull-right"
          >
            {{ $t('actions.updateResults') }}
          </button>
        </li>
      </ul>
    </form>

    <div class="helpers">
      <span class="helpers__title">{{ $t('common.actions') }}:</span>
      <a href="#" class="btn btn-regular btn-mobile-full" @click.prevent="deleteSelected"
        ><i class="icon-trash"></i> {{ $t('pages.messages.actions.deleteSelected') }}</a
      >
      <a href="#" class="btn btn-regular btn-mobile-full" @click.prevent="markSelectedAsRead"
        ><i class="icon-mail"></i> {{ $t('pages.messages.actions.markAsRead') }}</a
      >
    </div>

    <BaseTable
      v-model="selectedMessages"
      :headers="messageTableHeaders"
      :highlight-row="(item) => item.status.code === 'UNREAD'"
      :items="messages"
      :pagination="messagePagination"
      :loading="loadingMessages"
      :on-row-click="viewMessage"
      enable-sorting
      @update:page="getMessages"
      @update:sorting="setOrderBy"
    >
      <template #item.type="{ item }">
        {{ item.type && item.type.name }}
      </template>
      <template #item.category="{ item }">
        {{ item.category && item.category.name }}
      </template>
      <template #item.messageDate="{ item }">
        {{ $formatDate(item.messageDate, 'yyyy-MM-dd HH:mm') }}
      </template>
    </BaseTable>
  </div>
</template>

<script>
import BaseSelect from './base/BaseSelect';
import BaseDatepicker from './base/BaseDatepicker';
import classifierService from '../services/classifier.service';
import messageService from '../services/message.service';
import BaseTable from './base/BaseTable';
import { getCurrentPageAfterDeleting } from '@/util/pagination';

export default {
  name: 'MessagesReceived',

  components: { BaseTable, BaseDatepicker, BaseSelect },

  props: {
    messageTypes: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      loadingMessages: false,
      selectedMessages: [],
      messages: [],
      messagePagination: {},
      messageStatuses: [],
      messageCategories: [],
      messageParams: {},
    };
  },

  computed: {
    messageTableHeaders() {
      const translationKey = 'pages.messages.messageTable.headers';
      return [
        { text: this.$t(`${translationKey}.type`), value: 'type' },
        { text: this.$t(`${translationKey}.category`), value: 'category' },
        { text: this.$t(`${translationKey}.subject`), value: 'subject' },
        { text: this.$t(`${translationKey}.messageDate`), value: 'messageDate' },
        { text: this.$t(`${translationKey}.term`), value: 'term' },
        { text: this.$t(`${translationKey}.sender`), value: 'sender' },
      ];
    },
  },

  created() {
    this.getMessages();
    classifierService.getMessageStatuses().then((res) => {
      this.messageStatuses = res.data.messageStatus;
    });
    classifierService.getMessageCategories().then((res) => {
      this.messageCategories = res.data.messageCategory;
    });
  },

  mounted() {
    window.addEventListener('pageshow', this.handlePageShow);
    this.$root.$on('important-message-read', this.handleImportantMessageRead);
  },

  beforeDestroy() {
    window.removeEventListener('pageshow', this.handlePageShow);
    this.$root.$off('important-message-read', this.handleImportantMessageRead);
  },

  methods: {
    deleteSelected() {
      if (!this.selectedMessages.length) {
        return;
      }
      const messageIds = this.selectedMessages.map((m) => m.id);
      this.loadingMessages = true;
      messageService
        .delete(messageIds)
        .then(() => {
          this.getMessages(getCurrentPageAfterDeleting(this.messagePagination));
        })
        .catch(() => {
          this.loadingMessages = false;
        });
    },

    getMessages(page = 1) {
      this.loadingMessages = true;
      const params = { ...this.messageParams, page };
      messageService
        .getList(params)
        .then((res) => {
          this.messages = res.data.message;
          this.selectedMessages = [];
          this.messagePagination = {
            page: res.data.page,
            total: res.data.total,
            pageSize: res.data.pageSize,
          };
        })
        .catch(() => {
          this.messages = [];
          this.messagePagination = {};
        })
        .finally(() => {
          this.loadingMessages = false;
        });
    },

    markSelectedAsRead() {
      if (!this.selectedMessages.length) {
        return;
      }
      this.loadingMessages = true;
      const messageIds = this.selectedMessages.map((m) => m.id);
      messageService
        .markAsRead(messageIds)
        .then((res) => {
          this.messages = this.messages.map((m) =>
            res.data.message.indexOf(m.id) > -1
              ? { ...m, status: { name: 'perskaitytas', code: 'READ' } }
              : m
          );
        })
        .finally(() => {
          this.loadingMessages = false;
        });
    },

    setOrderBy(string) {
      this.$set(this.messageParams, 'orderBy', string);
      this.getMessages();
    },

    viewMessage(message) {
      window.location.href = this.$url(`${this.$t('pages.message.url')}?id=${message.id}`);
    },

    handlePageShow(event) {
      if (event.persisted) {
        const currentPage = this.messagePagination.page || 1;
        this.getMessages(currentPage);
      }
    },
    handleImportantMessageRead() {
      const currentPage = this.messagePagination.page || 1;
      this.getMessages(currentPage);
    },
  },
};
</script>
